import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Folder, Plus, Sparkles, FolderPlus, FileText, Info, HardHat } from "lucide-react";
import { useNavigate } from "react-router-dom";
import CreateAssessmentDialog from "../../CreateAssessmentDialog";
import CreateFolderDialog from "../../CreateFolderDialog";
import type { Database } from "@/integrations/supabase/types";

type FolderType = Database['public']['Tables']['folders']['Row'];

interface ModernEmptyStateProps {
  folderData: FolderType | null;
  projectId: string;
  folderId: string;
  isShared: boolean;
  onStartAssessment?: (projectId: string, folderId: string) => void;
  onCreateFolder: (name: string, description?: string) => Promise<void>;
}

export const ModernEmptyState: React.FC<ModernEmptyStateProps> = ({
  folderData,
  projectId,
  folderId,
  isShared,
  onStartAssessment,
  onCreateFolder,
}) => {
  const navigate = useNavigate();
  const [isCreateAssessmentDialogOpen, setIsCreateAssessmentDialogOpen] = useState(false);
  const [isCreateFolderDialogOpen, setIsCreateFolderDialogOpen] = useState(false);
  const [folderName, setFolderName] = useState("");

  const handleCreateAssessmentSuccess = () => {
    setIsCreateAssessmentDialogOpen(false);
  };

  const handleCreateFolderSubmit = async () => {
    if (!folderName.trim()) return;
    await onCreateFolder(folderName.trim());
    setFolderName("");
    setIsCreateFolderDialogOpen(false);
  };

  return (
    <>
      <div className="flex items-center justify-center min-h-[500px] px-4">
        <Card className="max-w-2xl w-full glassmorphism border-dashed border-2 border-primary/30 backdrop-blur-sm shadow-xl">
          <CardContent className="p-12 text-center space-y-8 bg-gradient-to-br from-card/90 to-card/60">
            {/* Icon */}
            <div className="flex justify-center">
              <div className="p-6 bg-gradient-to-br from-primary/20 to-primary/10 rounded-2xl backdrop-blur-sm border border-primary/30 shadow-lg">
                <Folder className="h-16 w-16 text-primary" />
              </div>
            </div>
            
            {/* Heading */}
            <div className="space-y-3">
              <h3 className="text-2xl font-bold text-foreground">
                {folderData ? `Mappen "${folderData.name}" är tom` : "Projektet är tomt"}
              </h3>
              <p className="text-muted-foreground text-lg leading-relaxed max-w-md mx-auto">
                {folderData 
                  ? "Kom igång genom att skapa din första risksammanställning eller undermapp"
                  : "Kom igång genom att skapa din första mapp för att organisera dina risksammanställningar"
                }
              </p>
            </div>

            {/* Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left max-w-2xl mx-auto">
              <div className="flex items-start gap-4 p-5 glassmorphism border border-primary/30 rounded-xl backdrop-blur-sm">
                <div className="p-2 bg-primary/20 rounded-lg">
                  <FolderPlus className="h-5 w-5 text-primary" />
                </div>
                <div className="text-sm">
                  <p className="font-semibold text-foreground mb-1">
                    {folderData ? "Skapa undermappar" : "Skapa mappar"}
                  </p>
                  <p className="text-muted-foreground text-xs leading-relaxed">
                    {folderData 
                      ? "Organisera dina risksammanställningar i logiska grupper"
                      : "Skapa mappar för att organisera och gruppera dina risksammanställningar"
                    }
                  </p>
                </div>
              </div>
              
              {onStartAssessment && folderData && (
                <div className="flex items-start gap-4 p-5 glassmorphism border border-emerald-500/30 rounded-xl backdrop-blur-sm">
                  <div className="p-2 bg-emerald-500/20 rounded-lg">
                    <FileText className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div className="text-sm">
                    <p className="font-semibold text-foreground mb-1">Ny risksammanställning</p>
                    <p className="text-muted-foreground text-xs leading-relaxed">
                      Starta en ny riskbedömning för denna mapp
                    </p>
                  </div>
                </div>
              )}
              
              {folderData && (
                <div className="flex items-start gap-4 p-5 glassmorphism border border-orange-500/30 rounded-xl backdrop-blur-sm">
                  <div className="p-2 bg-orange-500/20 rounded-lg">
                    <HardHat className="h-5 w-5 text-orange-600" />
                  </div>
                  <div className="text-sm">
                    <p className="font-semibold text-foreground mb-1">Ny arbetsberedning</p>
                    <p className="text-muted-foreground text-xs leading-relaxed">
                      Skapa arbetsberedning från risksammanställning eller från scratch
                    </p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button 
                onClick={() => setIsCreateFolderDialogOpen(true)}
                variant="outline"
                size="lg"
                className="flex items-center gap-3 h-14 text-base font-medium glassmorphism border-primary/30 hover:bg-primary/10 backdrop-blur-sm"
              >
                <FolderPlus className="h-5 w-5" />
                Skapa mapp
              </Button>
              
              {onStartAssessment && folderData && (
                <Button 
                  onClick={() => setIsCreateAssessmentDialogOpen(true)}
                  size="lg"
                  className="flex items-center gap-3 h-14 text-base font-medium bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <FileText className="h-5 w-5" />
                  Ny risksammanställning
                  <Sparkles className="h-4 w-4 opacity-80" />
                </Button>
              )}
              
              {folderData && (
                <Button 
                  onClick={() => navigate(`/arbetsberedning/${projectId}/${folderId}`)}
                  variant="outline"
                  size="lg"
                  className="flex items-center gap-3 h-14 text-base font-medium glassmorphism border-orange-500/30 hover:bg-orange-500/10 backdrop-blur-sm"
                >
                  <HardHat className="h-5 w-5" />
                  Ny arbetsberedning
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {onStartAssessment && folderData && (
        <CreateAssessmentDialog
          isOpen={isCreateAssessmentDialogOpen}
          onOpenChange={setIsCreateAssessmentDialogOpen}
          projectId={projectId}
          folderId={folderId}
          onSuccess={handleCreateAssessmentSuccess}
          isSharedFolder={isShared}
        />
      )}

      <CreateFolderDialog
        isOpen={isCreateFolderDialogOpen}
        onOpenChange={setIsCreateFolderDialogOpen}
        onSubmit={handleCreateFolderSubmit}
        folderName={folderName}
        setFolderName={setFolderName}
      />
    </>
  );
};